import { createColumnHelper } from '@tanstack/react-table';
import { format } from 'date-fns';
import { InfoIcon, RotateCcw, Trash2Icon } from 'lucide-react';

import {
  ScrollableCellWithAvatar,
  ScrollableCellWithColorDot,
} from '@/components/data-table/cells';
import { Button } from '@/components/ui/button';
import i18n from '@/core/i18n';
import { formatAmountWithCurrencyCode } from '@/helpers/finance';
import { capitalizeFirst } from '@/lib/utils';
import { StatusBadge, StatusBadgeType } from '@/modules/common';
import { ScrollableText } from '@/modules/planner/components/assignment/text/scrollable-text';

import { InvoiceListItem, InvoiceStatus, InvoiceSyncOperation } from '../types';
import { FORMAT_DATE_SHORT_MONTH_YEAR } from './date-format';

const columnHelper = createColumnHelper<InvoiceListItem>();

const getTextStyleByStatus = (status: InvoiceStatus) => ({
  color:
    status === InvoiceStatus.InProgress || status === InvoiceStatus.Error
      ? 'hsl(var(--muted-foreground))'
      : 'hsl(var(--foreground))',
});

export const getInvoicesTableColumns = ({
  onDeleteInvoice,
  onErrorInfo,
  onInProgressInfo,
  onRetry,
  isRetrying,
  onErrorInvoiceSelect,
}: {
  onDeleteInvoice?: (invoice: InvoiceListItem) => void;
  onErrorInfo?: (invoice: InvoiceListItem) => void;
  onInProgressInfo: (invoice: InvoiceListItem) => void;
  onRetry?: (invoiceId: number) => void;
  isRetrying?: (invoiceId: number) => boolean;
  onErrorInvoiceSelect?: (invoice: InvoiceListItem) => void;
}) => [
  columnHelper.accessor('number', {
    header: i18n.t('authenticated.invoices:headers.invoiceNumber'),
    enableHiding: false,
    cell: ({ getValue, row }) => {
      const { status, syncOperation } = row.original;
      const noValue = i18n.t('common:table.noValue');

      if (status === InvoiceStatus.InProgress && syncOperation === InvoiceSyncOperation.Create)
        return (
          <span className="text-muted-foreground">
            {i18n.t('authenticated.invoices:common.invoiceInProgress')}
          </span>
        );

      if (status === InvoiceStatus.Error)
        return <span className="text-muted-foreground">{noValue}</span>;

      return (
        <div className="flex items-center gap-3">
          <ScrollableText className="whitespace-nowrap">
            {getValue() ? `#${getValue()}` : noValue}
          </ScrollableText>
        </div>
      );
    },
  }),
  columnHelper.accessor(row => row.project?.name, {
    id: 'project',
    header: i18n.t('authenticated.invoices:headers.project'),
    enableHiding: false,
    minSize: 200,
    size: 200,
    maxSize: 400,
    cell: ({ getValue, row }) => {
      const projectName = getValue();
      return projectName ? (
        <ScrollableCellWithColorDot
          text={projectName}
          image={row.original.project?.logoUrl}
          avatarName={projectName}
          avatarSize="sm"
          alt={`${projectName} logo`}
          dotColor={row.original.project?.colorHex || 'hsl(var(--muted-foreground))'}
          textStyle={getTextStyleByStatus(row.original.status)}
        />
      ) : (
        i18n.t('common:table.noValue')
      );
    },
  }),
  columnHelper.accessor(row => row.client?.name, {
    id: 'client',
    header: i18n.t('authenticated.invoices:headers.client'),
    minSize: 200,
    size: 200,
    maxSize: 400,
    cell: ({ getValue, row }) => {
      const clientName = getValue();
      return clientName ? (
        <ScrollableCellWithAvatar
          text={clientName}
          image={row.original.client?.logoUrl}
          avatarName={clientName}
          avatarSize="sm"
          alt={`${clientName} logo`}
          textStyle={getTextStyleByStatus(row.original.status)}
        />
      ) : (
        i18n.t('common:table.noValue')
      );
    },
  }),
  columnHelper.accessor('status', {
    header: () => (
      <div className="flex w-full justify-center">
        {i18n.t('authenticated.invoices:headers.status')}
      </div>
    ),
    enableHiding: false,
    minSize: 100,
    size: 150,
    maxSize: 240,
    meta: { cellClassName: 'text-center' },
    cell: ({ getValue }) => {
      const status = getValue() || InvoiceStatus.Unknown;

      return (
        <StatusBadge
          type={StatusBadgeType.Invoice}
          status={status}
          label={capitalizeFirst(status)}
        />
      );
    },
  }),
  columnHelper.accessor('issuedDate', {
    header: i18n.t('authenticated.invoices:headers.issuedDate'),
    cell: ({ getValue, row }) => {
      const dateString = getValue();
      if (!dateString) return i18n.t('common:table.noValue');
      try {
        const date = new Date(dateString);
        return (
          <span className="whitespace-nowrap" style={getTextStyleByStatus(row.original.status)}>
            {format(date, FORMAT_DATE_SHORT_MONTH_YEAR)}
          </span>
        );
      } catch {
        return (
          <span className="whitespace-nowrap" style={getTextStyleByStatus(row.original.status)}>
            {dateString?.toString() ?? i18n.t('common:table.noValue')}
          </span>
        );
      }
    },
  }),
  // The billed work period, mirroring the bills list. `period` is already formatted by
  // `mapInvoiceSummaryFromResponse`, so this column only renders it.
  columnHelper.accessor('period', {
    header: i18n.t('authenticated.invoices:headers.period'),
    size: 200,
    cell: ({ getValue, row }) => (
      <div className="flex min-w-[200px] items-center">
        <span className="whitespace-nowrap" style={getTextStyleByStatus(row.original.status)}>
          {getValue() || i18n.t('common:table.noValue')}
        </span>
      </div>
    ),
  }),
  columnHelper.accessor('dueDate', {
    header: i18n.t('authenticated.invoices:headers.endDate'),
    cell: ({ getValue, row }) => {
      const dateString = getValue();
      if (!dateString) return i18n.t('common:table.noValue');
      try {
        const date = new Date(dateString);
        return (
          <span className="whitespace-nowrap" style={getTextStyleByStatus(row.original.status)}>
            {format(date, FORMAT_DATE_SHORT_MONTH_YEAR)}
          </span>
        );
      } catch {
        return (
          <span className="whitespace-nowrap" style={getTextStyleByStatus(row.original.status)}>
            {dateString?.toString() ?? i18n.t('common:table.noValue')}
          </span>
        );
      }
    },
  }),
  columnHelper.accessor('amount', {
    header: i18n.t('authenticated.invoices:headers.amount'),
    enableHiding: false,
    cell: ({ getValue, row }) => (
      <div style={{ minWidth: '8rem' }} className="flex items-center">
        <span style={getTextStyleByStatus(row.original.status)}>
          {formatAmountWithCurrencyCode(getValue(), row.original.currencyCode)}
        </span>
      </div>
    ),
  }),
  columnHelper.accessor('balance', {
    header: i18n.t('authenticated.invoices:headers.balance'),
    cell: ({ getValue, row }) => (
      <div style={{ minWidth: '8rem' }} className="flex items-center">
        <span style={getTextStyleByStatus(row.original.status)}>
          {formatAmountWithCurrencyCode(getValue(), row.original.currencyCode)}
        </span>
      </div>
    ),
  }),
  columnHelper.display({
    id: 'action',
    header: () => (
      <div className="flex w-full justify-center">
        {i18n.t('authenticated.invoices:headers.action')}
      </div>
    ),
    enableHiding: false,
    meta: { cellClassName: 'text-center' },
    cell: ({ row }) => {
      const { status } = row.original;

      if (status === InvoiceStatus.InProgress) {
        return (
          <div className="flex items-center justify-center">
            <Button
              size={'icon'}
              variant={'ghost'}
              onClick={e => {
                e.stopPropagation();
                onInProgressInfo(row.original);
              }}
            >
              <InfoIcon size={16} />
            </Button>
          </div>
        );
      }

      if (status === InvoiceStatus.Error) {
        return (
          <div className="flex items-center justify-center gap-2">
            <Button
              size={'icon'}
              variant={'ghost'}
              onClick={e => {
                e.stopPropagation();
                onErrorInvoiceSelect?.(row.original);
                onErrorInfo?.(row.original);
              }}
            >
              <InfoIcon size={16} />
            </Button>
            {onRetry && (
              <Button
                size={'icon'}
                variant={'ghost'}
                onClick={e => {
                  e.stopPropagation();
                  onRetry(row.original.id);
                }}
                disabled={isRetrying?.(row.original.id)}
                title={i18n.t('authenticated.invoices:table.retryInvoice')}
              >
                <RotateCcw size={16} />
              </Button>
            )}
          </div>
        );
      }

      return (
        <div className="flex items-center justify-center">
          {onDeleteInvoice && (
            <Button
              size={'icon'}
              variant={'ghost'}
              onClick={e => {
                e.stopPropagation();
                onDeleteInvoice(row.original);
              }}
            >
              <Trash2Icon size={16} />
            </Button>
          )}
        </div>
      );
    },
    size: 50,
  }),
];
